import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAgent } from '../../api/agents';
import { createSubAgent } from '../../api/subAgents';
import { getApiErrorMessage } from '../../utils/errors';
import { DEFAULT_SKILLS_INSTRUCTIONS, buildSubAgentPayload } from './constants';
import KnowledgeChoice from './steps/KnowledgeChoice';
import KnowledgeSetup from './steps/KnowledgeSetup';
import AgentSetup from './steps/AgentSetup';

const STEPS = {
  KNOWLEDGE_CHOICE: 'knowledge-choice',
  KNOWLEDGE_SETUP: 'knowledge-setup',
  AGENT_SETUP: 'agent-setup',
};

function OnboardingWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(STEPS.KNOWLEDGE_CHOICE);
  const [wizardTotalSteps, setWizardTotalSteps] = useState(3);
  const [knowledgeBaseId, setKnowledgeBaseId] = useState(null);
  const [wizardKnowledgeBase, setWizardKnowledgeBase] = useState(null);
  const [knowledgeForm, setKnowledgeForm] = useState({ name: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleKnowledgeSuccess = (data) => {
    setKnowledgeBaseId(data.knowledgeBaseId);
    setWizardKnowledgeBase({
      id: data.knowledgeBaseId,
      name: data.name,
      status: data.status,
      sourceType: data.sourceType,
      sourceRef: data.sourceRef,
    });
    setKnowledgeForm({ name: data.name });
    setCurrentStep(STEPS.AGENT_SETUP);
  };

  const handleAgentSubmit = async (formData) => {
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const agent = await createAgent({
        name: formData.agentName,
        tone_of_voice: formData.toneOfVoice,
        llm_model_line_id: formData.llmModelLineId,
        llm_effort_id: formData.llmEffortId,
        configurations: formData.configurations,
      });

      let hasManualSkillsSubAgent = false;

      for (let index = 0; index < formData.subAgents.length; index += 1) {
        const subAgent = formData.subAgents[index];
        await createSubAgent(
          agent.id,
          buildSubAgentPayload(subAgent, index, formData.subAgents)
        );

        if (subAgent.type === 'skills') {
          hasManualSkillsSubAgent = true;
        }
      }

      if (knowledgeBaseId && !hasManualSkillsSubAgent) {
        await createSubAgent(agent.id, {
          type: 'skills',
          name: 'conhecimento_principal',
          instructions: DEFAULT_SKILLS_INSTRUCTIONS,
          execution_phase: 'pre_response',
          knowledge_base_ids: [knowledgeBaseId],
          configurations: {},
        });
      }

      navigate('/agents', {
        replace: true,
        state: { onboardingSuccess: true, agentName: formData.agentName },
      });
    } catch (err) {
      setSubmitError(getApiErrorMessage(err, 'Não foi possível criar o agente.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (currentStep === STEPS.KNOWLEDGE_CHOICE) {
    return (
      <KnowledgeChoice
        totalSteps={wizardTotalSteps}
        onYes={() => {
          setWizardTotalSteps(3);
          setCurrentStep(STEPS.KNOWLEDGE_SETUP);
        }}
        onSkip={() => {
          setWizardTotalSteps(2);
          setCurrentStep(STEPS.AGENT_SETUP);
        }}
      />
    );
  }

  if (currentStep === STEPS.KNOWLEDGE_SETUP) {
    return (
      <KnowledgeSetup
        totalSteps={wizardTotalSteps}
        initialValues={knowledgeForm}
        onBack={() => setCurrentStep(STEPS.KNOWLEDGE_CHOICE)}
        onSuccess={handleKnowledgeSuccess}
      />
    );
  }

  return (
    <AgentSetup
      stepNumber={wizardTotalSteps}
      totalSteps={wizardTotalSteps}
      hasKnowledge={Boolean(knowledgeBaseId)}
      wizardKnowledgeBase={wizardKnowledgeBase}
      onBack={() => {
        if (wizardTotalSteps === 3) {
          setCurrentStep(STEPS.KNOWLEDGE_SETUP);
        } else {
          setCurrentStep(STEPS.KNOWLEDGE_CHOICE);
        }
      }}
      onSubmit={handleAgentSubmit}
      isSubmitting={isSubmitting}
      submitError={submitError}
    />
  );
}

export default OnboardingWizard;
